import { createContext, useContext, type ReactNode } from "react";
import { type MobileVideoStore } from "./mobileVideoStore";

interface VideoStoreContextValue {
  store: MobileVideoStore;
  // False when writes must be suppressed (web impersonation). Read-only
  // consumers ignore it; mutating hooks/components gate on it.
  canWrite: boolean;
}

const VideoStoreContext = createContext<VideoStoreContextValue | null>(null);

export function VideoStoreProvider({
  store,
  canWrite,
  children,
}: {
  store: MobileVideoStore;
  canWrite: boolean;
  children: ReactNode;
}) {
  return (
    <VideoStoreContext.Provider value={{ store, canWrite }}>
      {children}
    </VideoStoreContext.Provider>
  );
}

export function useVideoStoreContext(): VideoStoreContextValue {
  const value = useContext(VideoStoreContext);

  if (value == null) {
    throw new Error(
      "useVideoStoreContext must be used within VideoStoreProvider",
    );
  }

  return value;
}

export function useVideoStore(): MobileVideoStore {
  return useVideoStoreContext().store;
}

// Non-throwing variant for consumers that may render outside a provider
// (e.g. the web VideoPlayer in contexts without the funder QueueProvider).
export function useVideoStoreOptional(): MobileVideoStore | null {
  return useContext(VideoStoreContext)?.store ?? null;
}

export function useVideoStoreCanWrite(): boolean {
  return useVideoStoreContext().canWrite;
}
